import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductWithStock, ItemWithStock, Product } from '@/lib/utils/database';
import { Item } from '@/redux/back-dashboard-api';
import { ItemImage } from '../../containers/item-image';
import { useWebshopMode } from '@/components/page-widgets/webshop/context';
import styles from './index.module.css';
import colorPickerStyles from '../../forms/color-picker/index.module.css';
import { ItemStockStatus } from '@/components/badges/item-stock-status';
import { AddProductToBagButton } from '@/components/buttons/add-product-to-bag-button';
import { ItemDescription } from '../item-description';

export const ShopProductGrid: FC<{
  children?: React.ReactNode;
}> = ({ children }) => <div className={styles['product-grid']}>{children}</div>;

const ShopProductItems: FC<{
  selected: string;
  items: ItemWithStock[];
  onSelect: (id: string) => void;
}> = ({ items, selected, onSelect }) => {
  const previewItem = items.find(({ _id }) => _id === selected);
  const otherPreviewItems = items.reduce((acc, { _id, imageUrls }) => {
    if (imageUrls[0]) acc[imageUrls[0]] = _id;
    return acc;
  }, {} as Record<string, string>);

  return (
    <>
      {Object.entries(otherPreviewItems).map(([imageUrl, id]) => (
        <div key={id}>
          <div
            className={`w-[40px] ${styles['product__combination']} ${
              previewItem?.imageUrls[0] === imageUrl
                ? styles['product__combination--selected']
                : ''
            }`}
            role="button"
            onClick={() => onSelect(id)}
          >
            <div
              className="w-full min-h-[40px] bg-contain bg-no-repeat bg-center"
              style={{
                backgroundImage: `url("${imageUrl}")`,
              }}
            ></div>
          </div>
        </div>
      ))}
    </>
  );
};

const ProductColors: FC<{
  product: Product;
}> = ({ product }) => {
  // Hooks
  const { t } = useTranslation();
  // Data
  const colors = [...product.combinations.color];

  return (
    <>
      {colors.length > 0 && (
        <div className="flex items-center gap-2 mt-2">
          <span>{t('label.availableColors')}</span>

          <div className="flex gap-2">
            {colors.map((color) => (
              <div
                key={color}
                className={`${colorPickerStyles['color__circle']} ${
                  colorPickerStyles[`color__circle--${color.toLowerCase()}`]
                } `}
                title={color}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export const ShopProduct: FC<{
  product: ProductWithStock;
  onAddToCart: (product: ProductWithStock, preferredItem: Item) => void;
  onClick: (product: ProductWithStock, preferredItem: Item) => void;
}> = ({ product, onAddToCart, onClick }) => {
  const { showStock, allowOutOfStockOrders } = useWebshopMode();
  // Hooks
  const { itemRef } = product;
  const [combinationId, setCombinationId] = useState<string>(itemRef._id);
  // Data
  const selectedCombinationItem = product.items.find(
    ({ _id }) => _id === combinationId
  ) as ItemWithStock;
  const totalStock = product.items.reduce(
    (acc, item) => acc + (item.stock.available || 0),
    0
  );
  const hasStock = allowOutOfStockOrders || totalStock > 0;

  // Handlers
  const onClickProduct = useCallback(() => {
    hasStock && onClick(product, selectedCombinationItem || product.itemRef);
  }, [hasStock, onClick, product, selectedCombinationItem]);

  const canOrder = !showStock || hasStock;
  const onSelectProduct = useCallback(
    () =>
      canOrder &&
      onAddToCart(product, selectedCombinationItem || product.itemRef),
    [canOrder, onAddToCart, product, selectedCombinationItem]
  );

  return (
    <div data-testid="product" className={styles['product']}>
      <div className="w-full h-[310px] relative">
        <ItemImage
          className="h-full"
          onClick={onClickProduct}
          item={selectedCombinationItem}
        />
      </div>

      <div className="mt-4">
        <div className="mt-4 flex flex-row space-x-2">
          <ShopProductItems
            items={product.items}
            selected={combinationId}
            onSelect={setCombinationId}
          />
        </div>
        <h3
          className={`${styles['product__name']} cursor-pointer`}
          role="button"
          onClick={onClickProduct}
        >
          {product.itemRef.productName.toUpperCase()}
        </h3>

        <div className={styles['product__description']}>
          <ItemDescription
            description={product.itemRef.description}
            summarized
          />
        </div>
      </div>

      <ProductColors product={product} />

      {showStock && (
        <div className="mt-4">
          <ItemStockStatus stock={totalStock} />
        </div>
      )}

      <AddProductToBagButton
        product={product}
        onAddToBag={onSelectProduct}
        allowOutOfStockOrders={allowOutOfStockOrders}
      />
    </div>
  );
};
